import { Descriptions, Table, Typography, Tag } from 'antd';
import React, { CSSProperties, useEffect, useState } from 'react';
import {
    chemblTargetExternalLink,
    chineseDrugLink,
    diseaseLink,
    keggPathExternalLink,
    keggTargetExternalLink,
    plantDetailLink,
    recipeLink,
    uniprotDetailLink,
    uniprotTargetExternalLink,
} from '../hooks/TableLink';
import { useHistory, useParams, Link } from 'react-router-dom';

import useFetch from '../hooks/fetcher';
import _ from 'lodash';

const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';

const { Title } = Typography;
const { Item } = Descriptions;
const h3Style: CSSProperties = {
    marginTop: '20px',
};
const uniprotTags = (uniprotList?: string) => {
    if (_.isNil(uniprotList)) {
        return null;
    }
    return uniprotList.split(',').map((item: string, key: number) => {
        return (
            <Link to={`/target/${item}`} key={key}>
                <Tag color="blue" style={{ cursor: 'pointer', margin: '3px' }}>
                    {item}
                </Tag>
            </Link>
        );
    });
};
const columnTargets = [
    { title: 'Targets', dataIndex: 'protein', key: 'protein', render: uniprotDetailLink },
    { title: 'Gene', dataIndex: 'gene', key: 'gene' },
    { title: 'ChEMBL ID', dataIndex: 'chembl_id', key: 'chembl_id', render: chemblTargetExternalLink },
    { title: 'UNIPROT ID', dataIndex: 'uniprot_entry', key: 'uniprot_entry', render: uniprotTargetExternalLink },
    { title: 'KEGG ID', dataIndex: 'kegg_id', key: 'kegg_id', render: keggTargetExternalLink },
    { title: 'Pharmacological Action', dataIndex: 'pharmacological_action', key: 'pharmacological_action' },
    { title: 'Druggable', dataIndex: 'druggable', key: 'druggable' },
];
const columnsDiseases = [
    {
        title: 'Disease Code',
        key: 'level4',
        dataIndex: 'level4',
        render: diseaseLink,
    },
    {
        title: 'Disease Annotation',
        key: 'level4_description',
        dataIndex: 'level4_description',
    },
    {
        title: 'Targets',
        key: 'targets',
        dataIndex: 'targets',
        width: 350,
        render: uniprotTags,
    },
];
const columnsPathways = [
    { title: 'KEGG Pathway', dataIndex: 'pathway_id', key: 'pathway_id', render: keggPathExternalLink },
    { title: 'Pathway Name', dataIndex: 'pathway_name', key: 'pathway_name' },
    {
        title: 'Count Druggable Targets',
        dataIndex: 'count_current_druggable_targets',
        key: 'count_current_druggable_targets',
    },
    {
        title: 'PValue',
        dataIndex: 'p21',
        key: 'p21',
    },
    {
        title: 'EValue',
        dataIndex: 'e21',
        key: 'e21',
    },
];
const columnRecipes = [
    {
        title: 'Chinese Character',
        key: 'chinese_character',
        dataIndex: 'chinese_character',
        render: recipeLink,
    },
    {
        title: 'Transliteration',
        key: 'pinyin',
        dataIndex: 'pinyin',
    },
    {
        title: 'English Name',
        key: 'english_name',
        dataIndex: 'english_name',
    },
];
type IDetail = {
    ingredients_id: string;
    compound_name: string;
    iupac_name: string;
    smiles: string;
    inchi: string;
    inchi_key: string;
    pubchem_cid: string;
    chembl_id: string;
    atc: string;
};

type Ircpn = {
    ingredients_id: string;
    chinese_id: string;
    botanical_id: string;
    botanical_name: string;
    pinyin: string;
    chinese_pharmaceutical_name: string;
};

type IngredientDetailType = {
    detail: IDetail[];
    related_chinese_pharmaceutical_name: Ircpn[];
    synonyms: { synonyms: string }[];
    related_targets: {
        protein: string;
        gene: string;
        chembl_id: string;
        uniprot_entry: string;
        kegg_id: string;
        pharmacological_action: string;
        druggable: string;
    }[];
    related_diseases: RelatedDiseasesType[];
    related_pathways: {
        pathway_id: string;
        pathway_name: string;
        count_current_druggable_targets: number;
        p21: number;
        e21: number;
    }[];
    related_recipes: {
        recipes_id: string;
        chinese_character: string;
        pinyin: string;
        english_name: string;
    }[];
};
type RelatedDiseasesType = {
    id: string;
    level4: string;
    level4_description: string;
    targets: string;
};

const PageIngredientDetail: React.FC = () => {
    const [result, setResult] = useState<IngredientDetailType>();
    const { iid: searchId } = useParams<any>();
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const history = useHistory();
    const goToTargetDetail = (keyword: string): void => {
        history.push(`/target/${keyword}`);
    };
    useEffect(() => {
        get('/api/ingredient_detail.php', { ingredient_id: searchId }).then((r) => setResult(r));
    }, []);

    const columns = [
        {
            title: 'Accepted Botanical Name',
            key: 'botanical_name',
            dataIndex: 'botanical_name',
            render: plantDetailLink,
        },
        {
            title: 'Chinese Pharmaceutical Name',
            key: 'chinese_pharmaceutical_name',
            dataIndex: 'chinese_pharmaceutical_name',
            render: chineseDrugLink,
        },
        { title: 'Transliteration', key: 'pinyin', dataIndex: 'pinyin' },
    ];
    return (
        <div>
            <Title level={3}>Ingredient: {result?.detail[0].compound_name}</Title>
            <Title level={4}>Ingredient Details</Title>
            <Descriptions bordered colon column={1} size="middle" style={{ marginBottom: '3vh' }}>
                <Item label="Ingredient Name:">{result?.detail[0].compound_name}</Item>
                <Item label="IUPAC Name:">{result?.detail[0].iupac_name}</Item>
                <Item label="Synonyms:">{result?.synonyms.map((e) => e.synonyms).join(', ')}</Item>
                <Item label="Canonical SMILES:">{result?.detail[0].smiles}</Item>
                <Item label="Standard InCHI:">{result?.detail[0].inchi}</Item>
                <Item label="Standard InCHIKey:">{result?.detail[0].inchi_key}</Item>
                <Item label="Pubchem Cid:">
                    <a
                        href={`https://pubchem.ncbi.nlm.nih.gov/compound/${result?.detail[0].pubchem_cid}`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {result?.detail[0].pubchem_cid}
                    </a>
                </Item>
                <Item label="ChEMBL ID:">
                    <a
                        href={`https://www.ebi.ac.uk/chembl/compound_report_card/${result?.detail[0].chembl_id}`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {result?.detail[0].chembl_id}
                    </a>
                </Item>
                <Item label="ATC Code">{result?.detail[0].atc}</Item>
            </Descriptions>

            <Title level={4} style={h3Style}>
                Related Targets
            </Title>
            <Table columns={columnTargets} dataSource={result?.related_targets} style={{ marginBottom: '3vh' }} />

            <Title level={4}>Related KEGG Pathways</Title>
            <Table
                columns={columnsPathways}
                dataSource={result?.related_pathways}
                style={{ marginBottom: '3vh' }}
                // rowKey={(r): string => r.kegg_path_id}
            />

            <Title level={4}>Related Diseases</Title>
            <Table columns={columnsDiseases} dataSource={result?.related_diseases} style={{ marginBottom: '3vh' }} />
            <Title level={4} style={h3Style}>
                {' '}
                Related Herbs
            </Title>
            <Table
                dataSource={result?.related_chinese_pharmaceutical_name}
                columns={columns}
                rowKey={(record): string => record.chinese_id + Math.floor(Math.random() * 1000)}
            />
            <Title level={4}>Related Recipes</Title>
            <Table columns={columnRecipes} dataSource={result?.related_recipes} style={{ marginBottom: '3vh' }} />
        </div>
    );
};

export default PageIngredientDetail;
